import "../../../css/GlobalStyles.css";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import {
  AccessLevel,
  Event,
  EventType,
  MediaAspectRatio,
  OnboardingFlowType,
  ThemeType,
  TicketType,
} from "@markit/common.types";
import CreateProductWrapper from "../../../components/CreatorDashboard/Products/CreateProduct/CreateProductWrapper";
import { makeEmptyEvent } from "../../../utils/makeEvent";
import CreateProductTitleDescription from "../../../components/CreatorDashboard/Products/CreateProduct/CreateProductTitleDescription";
import { FileUploader } from "react-drag-drop-files";
import RectangleButton from "../../../components/Buttons/RectangleButton";
import { Colors } from "../../../utils/colors";
import { Icon } from "@iconify/react";
import AlertContainer from "../../../components/Containers/AlertContainer";
import {
  uploadVideo,
  errorMediaAlertText,
  errorMediaSubAlertText,
  generateVideoThumbnail,
  getVideoAspectRatio,
  MAX_MEDIA_PRODUCT_SIZE,
  supportedFileTypes,
  saveProductMediaToFirestore,
  buildFinalMediaUrls,
  deleteRemovedMediaFromStorage,
  MAX_MULTI_IMAGES,
} from "../../../utils/mediaUtils";
import {
  errorSubAlertText,
  errorAlertText,
  MEDIA_UPLOAD_CODES,
  uploadMedia,
  getImageAspectRatio,
  MAX_IMAGE_NO_COMPRESSION_SIZE,
} from "../../../utils/photoUtils";
import MediaPlayer from "../../../components/CreatorDashboard/Products/Components/MediaPlayer";
import PopupModalContainer from "../../../components/Containers/PopupModalContainer";
import ProgressBar from "@ramonak/react-progress-bar";
import { useLocation } from "react-router-dom";
import { useEventSelector } from "../../../hooks/useParametricSelector";
import PDFViewer from "../../../components/CreatorDashboard/Products/Components/PDFViewer";
import { UploadTask } from "firebase/storage";
import { HorizontalDivider } from "../../../components/Dividers/HorizontalDivider";
import { ContainerButton } from "../../../components/Buttons/ContainerButton";
import { LightTheme } from "../../../hooks/useTheme";
import CustomRichTextEditor from "../../../components/TextFields/CustomRichTextEditor";
import { useRichTextEditor } from "../../../hooks/useRichTextEditor";
import { useEditorState } from "@tiptap/react";
import { validateProductBody } from "../../../utils/productUtils";
import { useOnMount } from "../../../hooks/useOnMount";
import MediaPreviewCarousel from "../../../components/Media/MediaPreviewCarousel";
import { generate } from "shortid";
import {
  getCanonicalMediaUrls,
  getProductBaseStoragePath,
} from "@markit/common.utils";
import OnboardingTourStep from "../../../components/Onboarding/OnboardingTourStep";
import { SELL_PRODUCTS_ONBOARDING_STEPS } from "../../../utils/onboardingFlowUtils";

const getUploadStepFileText = (eventProductType: EventType) => {
  switch (eventProductType) {
    case EventType.VIDEO:
      return "Upload the video file you're selling.";
    case EventType.AUDIO:
      return "Upload the audio file you're selling.";
    case EventType.PDF:
      return "Upload the PDF file you're selling.";
    case EventType.CUSTOM_PRODUCT:
    default:
      return "Upload one or more images that showcase the product you're selling.";
  }
};

const getProductTypeFromPath = (pathname: string) => {
  return pathname.includes("video")
    ? EventType.VIDEO
    : pathname.includes("audio")
      ? EventType.AUDIO
      : pathname.includes("custom-product")
        ? EventType.CUSTOM_PRODUCT
        : EventType.PDF;
};

const CreateProduct = () => {
  const { accountData } = useSelector(getAccountState).account;
  const { pathname, state } = useLocation() || {};
  const { duplicatedEvent } = (state || {}) as { duplicatedEvent?: Event };
  const eventProductType = getProductTypeFromPath(pathname || "");
  const [tempProductData, setTempProductData] = useState<Event>(
    makeEmptyEvent(
      accountData.uid,
      accountData.isAdmin,
      undefined,
      eventProductType
    )
  );
  const [alertText, setAlertText] = useState<{
    header: string;
    subHeader: string;
  }>({ header: "", subHeader: "" });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadTaskRef = useRef<UploadTask | null>(null);
  const [showUploadInfo, setShowUploadInfo] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  const addMediaInputRef = useRef<HTMLInputElement | null>(null);

  const existingProductId = useEventSelector();
  const isEditing = useMemo(
    () => Boolean(existingProductId && !duplicatedEvent),
    [existingProductId, duplicatedEvent]
  );

  const mediaUrls = useMemo(
    () => getCanonicalMediaUrls(tempProductData),
    [tempProductData]
  );

  const isCustomProduct = useMemo(
    () => tempProductData.eventType === EventType.CUSTOM_PRODUCT,
    [tempProductData.eventType]
  );

  const { editor } = useRichTextEditor({
    body: tempProductData.body,
    styles: "min-height: 200px; overflow: scroll;",
    addCharacterCountExtension: {
      include: true,
      limit: 4000,
    },
    alternatePlaceholder: "Describe your product...",
  });

  const { charactersCount } = useEditorState({
    editor,
    selector: (context) => ({
      charactersCount: context.editor.storage.characterCount.characters(),
    }),
  });

  useOnMount(() => {
    if (duplicatedEvent) {
      setTempProductData({
        ...duplicatedEvent,
        eventType: eventProductType,
        mediaDownloadable:
          eventProductType === EventType.PDF
            ? (duplicatedEvent.mediaDownloadable ?? true)
            : duplicatedEvent.mediaDownloadable,
      });
    } else {
      const emptyEvent = makeEmptyEvent(
        accountData.uid,
        accountData.isAdmin,
        undefined,
        eventProductType
      );
      setTempProductData({
        ...emptyEvent,
        mediaDownloadable: eventProductType === EventType.PDF,
        accessLevel: AccessLevel.CHECKOUT,
        customTickets: [
          {
            ...emptyEvent.customTickets[0],
            type: TicketType.PAID,
            price: 1000,
          },
        ],
      });
    }
  });

  const handleMediaUpload = useCallback(
    (input: File | File[] | FileList) => {
      let files: File[] = [];
      if (input instanceof File) {
        files = [input];
      } else if (Array.isArray(input)) {
        files = input;
      } else if (input instanceof FileList) {
        files = Array.from(input);
      } else {
        return Promise.resolve();
      }

      if (files.length === 0) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve, reject) => {
        (async () => {
          setUploadLoading(true);
          try {
            if (isCustomProduct) {
              if (mediaUrls.length + files.length > MAX_MULTI_IMAGES) {
                setAlertText({
                  header: "Max number of images reached",
                  subHeader: `You can only upload up to ${MAX_MULTI_IMAGES} images. Remove some before adding more.`,
                });
                setUploadLoading(false);
                resolve();
                return;
              }
              // Support multiple media files for this type, other types multi support in the future
              for (const file of files) {
                const uploadData = await uploadMedia(file, false);
                if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
                  setAlertText({
                    header: errorAlertText(uploadData.uploadCode),
                    subHeader: errorSubAlertText(uploadData.uploadCode),
                  });
                  setUploadLoading(false);
                  resolve();
                  return;
                }
                const aspectRatio =
                  (await getImageAspectRatio(uploadData.blobUrl)) ||
                  MediaAspectRatio._16_9;
                setTempProductData((prev) => {
                  const existingUrls = getCanonicalMediaUrls(prev);
                  const allUrls = [...existingUrls, uploadData.blobUrl];
                  return {
                    ...prev,
                    mediaURLs: allUrls,
                    photoURL: allUrls[0],
                    mediaAspectRatio: prev.mediaAspectRatio || aspectRatio,
                  };
                });
              }
            } else {
              // Other types: single file
              const file = files[0];
              const uploadData = await uploadVideo(
                file,
                tempProductData.eventType
              );
              if (uploadData.uploadCode !== MEDIA_UPLOAD_CODES.SUCCESS) {
                setAlertText({
                  header: errorMediaAlertText(uploadData.uploadCode),
                  subHeader: errorMediaSubAlertText(
                    uploadData.uploadCode,
                    supportedFileTypes(tempProductData.eventType)
                  ),
                });
                setUploadLoading(false);
                resolve();
                return;
              }
              if (tempProductData.eventType === EventType.VIDEO) {
                const [thumbnailUrl, aspectRatio] = await Promise.all([
                  generateVideoThumbnail(uploadData.blobUrl, 0),
                  getVideoAspectRatio(uploadData.blobUrl),
                ]);
                setTempProductData((prev) => ({
                  ...prev,
                  photoURL: thumbnailUrl,
                  mediaURLs: [uploadData.blobUrl],
                  mediaAspectRatio: aspectRatio ?? prev.mediaAspectRatio,
                }));
              } else {
                setTempProductData((prev) => ({
                  ...prev,
                  mediaURLs: [uploadData.blobUrl],
                }));
              }
            }
          } catch (error) {
            console.error("Error during media upload:", error);
            reject(error);
          }
          setUploadLoading(false);
          resolve();
        })().catch((error) => {
          setUploadLoading(false);
          reject(error);
        });
      });
    },
    [isCustomProduct, mediaUrls.length, tempProductData.eventType]
  );

  const handleAddMedia = useCallback(() => {
    addMediaInputRef.current?.click();
  }, []);

  const removeMedia = useCallback(
    (index: number) => {
      const removedUrl = mediaUrls[index];
      const next = mediaUrls.filter((_, i) => i !== index);
      setTempProductData((prev) => ({
        ...prev,
        mediaURLs: next,
        mediaURL: "", // for backsupport
        // photoURL is the product social preview; keep in sync when it matched removed gallery media
        ...(prev.photoURL === removedUrl && { photoURL: next[0] ?? "" }),
      }));
      const newLength = mediaUrls.length - 1;
      setSelectedMediaIndex((i) => {
        if (i === index) {
          return Math.min(index, newLength - 1);
        }
        if (index < i) {
          return i - 1;
        }
        return i;
      });
    },
    [mediaUrls]
  );

  const reorderMedia = useCallback((newUrls: string[]) => {
    setTempProductData((prev) => {
      const oldFirst = getCanonicalMediaUrls(prev)[0];
      return {
        ...prev,
        mediaURLs: newUrls,
        ...(prev.photoURL === oldFirst && { photoURL: newUrls[0] ?? "" }),
      };
    });
  }, []);

  const validateOnPress = useCallback(() => {
    if (mediaUrls.length === 0) {
      setAlertText({
        header: `No ${tempProductData.eventType} Uploaded`,
        subHeader: "Please upload a file to continue",
      });
      return false;
    }
    if (!validateProductBody(editor)) {
      return false;
    }
    return true;
  }, [tempProductData.eventType, mediaUrls.length, editor]);

  const finalizeOnPress = useCallback(async () => {
    uploadTaskRef.current = null;
    try {
      const basePath = getProductBaseStoragePath(
        accountData.uid,
        tempProductData.id
      );
      const savedUrls = await buildFinalMediaUrls(mediaUrls, (url) =>
        saveProductMediaToFirestore(
          url,
          `${basePath}/${generate()}`,
          setUploadProgress,
          (uploadTask) => {
            uploadTaskRef.current = uploadTask;
          }
        )
      );

      // If not equal, some files failed to save into firebase storage, so return undefined
      if (savedUrls.length !== mediaUrls.length) {
        setUploadProgress(0);
        return undefined;
      }

      if (isEditing) {
        await deleteRemovedMediaFromStorage(
          tempProductData,
          savedUrls,
          (url) => {
            const decoded = decodeURIComponent(url);
            return decoded.includes(
              getProductBaseStoragePath(
                tempProductData.createdBy,
                tempProductData.id
              )
            );
          }
        );
      }

      return {
        ...tempProductData,
        mediaURLs: savedUrls,
        body: editor.getJSON(),
      } as Event;
    } catch {
      setUploadProgress(0);
      return undefined;
    }
  }, [accountData.uid, editor, isEditing, mediaUrls, tempProductData]);

  const handleCancelUpload = useCallback(() => {
    const confirmed = window.confirm(
      "Are you sure you want to cancel the upload?"
    );
    if (confirmed) {
      if (uploadTaskRef.current) {
        uploadTaskRef.current.cancel();
        uploadTaskRef.current = null;
        setUploadProgress(0);
      }
    }
  }, []);

  return (
    <>
      <CreateProductWrapper
        tempProductData={tempProductData}
        setTempProductData={setTempProductData}
        editor={editor}
        onValidate={validateOnPress}
        onFinalize={finalizeOnPress}
        createComp={
          <div className="ColumnNormal" style={{ gap: 30 }}>
            <div
              className="ColumnNormal onboarding-sell-products-titleupload-target"
              style={{ gap: 30 }}
            >
              <CreateProductTitleDescription
                tempProductData={tempProductData}
                setTempProductData={setTempProductData}
                titlePlaceholder={`${tempProductData.eventType} Title...`}
                descriptionPlaceholder="Add a subtitle..."
              />
              {mediaUrls.length > 0 ? (
                <div className="ColumnNormal" style={{ gap: 14 }}>
                  {tempProductData.eventType === EventType.PDF ? (
                    <PDFViewer
                      productData={tempProductData}
                      setProductData={setTempProductData}
                    />
                  ) : (
                    <MediaPlayer
                      productData={tempProductData}
                      setProductData={setTempProductData}
                      selectedMediaIndex={selectedMediaIndex}
                      setSelectedMediaIndex={setSelectedMediaIndex}
                      creatorName={accountData.fullName}
                      hardTheme={ThemeType.Light}
                      renderMediaPreviewCarousel={
                        <>
                          <input
                            ref={addMediaInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            multiple
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const chosen = e.target.files;
                              if (chosen?.length) {
                                handleMediaUpload(Array.from(chosen));
                              }
                              e.target.value = "";
                            }}
                          />
                          <MediaPreviewCarousel
                            event={tempProductData}
                            hardTheme={ThemeType.Light}
                            onAddMedia={handleAddMedia}
                            onSelectMedia={setSelectedMediaIndex}
                            onRemoveMedia={removeMedia}
                            onReorderMedia={reorderMedia}
                          />
                        </>
                      }
                    />
                  )}
                </div>
              ) : (
                <FileUploader
                  handleChange={handleMediaUpload}
                  name="file"
                  types={supportedFileTypes(tempProductData.eventType)}
                  multiple={isCustomProduct}
                  onTypeError={() =>
                    setAlertText({
                      header: errorMediaAlertText(MEDIA_UPLOAD_CODES.INVALID),
                      subHeader: errorMediaSubAlertText(
                        MEDIA_UPLOAD_CODES.INVALID,
                        supportedFileTypes(tempProductData.eventType)
                      ),
                    })
                  }
                  dropMessageStyle={{ opacity: 0.5 }}
                  disabled={uploadLoading}
                >
                  <div
                    className="ColumnCentering"
                    style={{
                      borderRadius: 20,
                      border: `1px solid ${Colors.GRAY2}`,
                      borderStyle: "dashed",
                      paddingBlock: 40,
                      gap: 10,
                      cursor: "pointer",
                    }}
                  >
                    <Icon
                      icon="uil:upload"
                      height={24}
                      style={{ color: Colors.BLACK }}
                    />
                    <span className="bodyMedium">
                      Upload {tempProductData.eventType}{" "}
                      {isCustomProduct ? "Images" : ""}
                    </span>
                    <span className="smallBodySubtext">
                      Supported formats are{" "}
                      {supportedFileTypes(tempProductData.eventType).join(", ")}{" "}
                      (max.{" "}
                      {(isCustomProduct
                        ? MAX_IMAGE_NO_COMPRESSION_SIZE
                        : MAX_MEDIA_PRODUCT_SIZE) / 1000000}
                      MB)
                    </span>
                    <div>
                      <RectangleButton
                        buttonLabel={`Select File${isCustomProduct ? "s" : ""}`}
                        onPress={() => {}}
                        altColor={Colors.BLACK}
                        altTextColor={Colors.WHITE}
                        containerStyles={{
                          padding: "10px 14px",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>
                </FileUploader>
              )}
            </div>
            <div
              className="descriptionTextAreaContainer"
              style={{ backgroundColor: Colors.TRANSPARENT, gap: 14 }}
            >
              <CustomRichTextEditor
                editor={editor}
                headerComp={<></>}
                isSimplified
              />
              <span className="bodySubtext">
                {charactersCount}/{4000}
              </span>
            </div>
            <AlertContainer
              headerComp={
                <div className="ColumnNormal" style={{ gap: 4 }}>
                  <Icon icon="uil:upload" height={40} />
                  <span className="sectionTitle">{alertText.header}</span>
                </div>
              }
              subHeaderComp={alertText.subHeader}
              closeModal={() => setAlertText({ header: "", subHeader: "" })}
              hideModal={alertText.header === ""}
            />
          </div>
        }
      />
      {!isEditing ? (
        <OnboardingTourStep
          key="sell-products-tour-step-2"
          flowType={OnboardingFlowType.SELL_PRODUCTS}
          target=".onboarding-sell-products-titleupload-target"
          content={
            <>
              Start with a title and add a description that provides context on
              your product offering.
              <br />
              <br />
              {getUploadStepFileText(eventProductType)}
            </>
          }
          stepNumber={2}
          totalSteps={SELL_PRODUCTS_ONBOARDING_STEPS}
          placement="right"
          skipScroll
        />
      ) : null}
      {uploadProgress > 0 && uploadProgress < 100 ? (
        <PopupModalContainer
          valueComp={
            showUploadInfo ? (
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <ContainerButton
                  onPress={() => setShowUploadInfo(false)}
                  theme={LightTheme}
                  iconName={"ion:chevron-back"}
                  iconSize={18}
                  containerStyles={{ width: 31, height: 31, padding: 0 }}
                />
                <div className="ColumnNormal" style={{ gap: 7 }}>
                  <span className="sectionTitle">Upload taking too long?</span>
                  <span className="bodySubtext">
                    For the fastest processing time, make sure your
                    video/audio/pdf format is standardized.
                  </span>
                  <span className="bodySubtext">
                    {tempProductData.eventType === EventType.VIDEO
                      ? "Use H.264 MP4 at 1080p for the best balance of quality and speed."
                      : tempProductData.eventType === EventType.AUDIO
                        ? "Use MP3 at 128kbps. High-fidelity files (WAV/FLAC) will take significantly longer to process."
                        : 'Use PDFs under 50MB and optimized for "Web View".'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="ColumnNormal" style={{ gap: 7 }}>
                <span className="bodyMedium">Uploading File...</span>
                <ProgressBar
                  completed={uploadProgress}
                  maxCompleted={100}
                  isLabelVisible={false}
                  bgColor={Colors.BLACK}
                  baseBgColor={Colors.WHITE1}
                  height="16px"
                  borderRadius="4px"
                />
                <div className="AlignedRowSpaced">
                  <span className="bodySubtext">
                    {uploadProgress}% complete
                  </span>
                  <Icon
                    icon="mdi:information-outline"
                    height={16}
                    style={{ cursor: "pointer", color: Colors.GRAY1 }}
                    onClick={() => setShowUploadInfo(true)}
                  />
                </div>
              </div>
            )
          }
          fixedBottomContent={
            !showUploadInfo ? (
              <div className="ColumnNormal" style={{ gap: 14, paddingTop: 14 }}>
                <HorizontalDivider altMargin={0} />
                <div className="AlignedRow" style={{ paddingInline: 14 }}>
                  <RectangleButton
                    buttonLabel="Cancel"
                    onPress={handleCancelUpload}
                    altColor={Colors.WHITE1}
                    altTextColor={Colors.BLACK}
                    containerStyles={{
                      padding: "14px 12px",
                    }}
                  />
                </div>
              </div>
            ) : undefined
          }
          noExit
          noDivider
          closeModal={() => {}}
          hideModal={false}
        />
      ) : null}
    </>
  );
};

export default memo(CreateProduct);
